import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// Types
export interface ScheduledPix {
  id: string;
  tenantId: string;
  accountId: string;
  userId: string;
  destinationKey: string;
  destinationKeyType: string;
  amount: number;
  description?: string;
  destinationName?: string;
  destinationDocument?: string;
  destinationBank?: string;
  scheduledDate: string;
  scheduledTime?: string;
  notificationSentAt?: string;
  status: 'SCHEDULED' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  processedAt?: string;
  endToEndId?: string;
  transactionId?: string;
  errorCode?: string;
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateScheduledPixDto {
  accountId: string;
  destinationKey: string;
  destinationKeyType: 'CPF' | 'CNPJ' | 'EMAIL' | 'PHONE' | 'EVP';
  amount: number;
  description?: string;
  scheduledDate: string;
  scheduledTime?: string;
}

export interface ScheduledPixQuery {
  status?: string;
  startDate?: string;
  endDate?: string;
  accountId?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface ScheduledPixListResponse {
  data: ScheduledPix[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ScheduledPixStats {
  total: number;
  scheduled: number;
  completed: number;
  failed: number;
  cancelled: number;
  totalAmount: number;
}

// Hooks
export function useScheduledPixList(query: ScheduledPixQuery = {}) {
  return useQuery<ScheduledPixListResponse>({
    queryKey: ['scheduled-pix', query],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (query.status) params.append('status', query.status);
      if (query.startDate) params.append('startDate', query.startDate);
      if (query.endDate) params.append('endDate', query.endDate);
      if (query.accountId) params.append('accountId', query.accountId);
      if (query.page) params.append('page', String(query.page));
      if (query.limit) params.append('limit', String(query.limit));
      if (query.sortBy) params.append('sortBy', query.sortBy);
      if (query.sortOrder) params.append('sortOrder', query.sortOrder);

      const response = await api.get(`/banking/pix/scheduled?${params.toString()}`);
      return response.data;
    },
  });
}

export function useScheduledPixDetail(id: string) {
  return useQuery<ScheduledPix>({
    queryKey: ['scheduled-pix', id],
    queryFn: async () => {
      const response = await api.get(`/banking/pix/scheduled/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useScheduledPixStats() {
  return useQuery<ScheduledPixStats>({
    queryKey: ['scheduled-pix-stats'],
    queryFn: async () => {
      const response = await api.get('/banking/pix/scheduled/stats');
      return response.data;
    },
  });
}

export function useCreateScheduledPix() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateScheduledPixDto) => {
      const response = await api.post('/banking/pix/scheduled', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-pix'] });
      queryClient.invalidateQueries({ queryKey: ['scheduled-pix-stats'] });
    },
  });
}

export function useCancelScheduledPix() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const response = await api.delete(`/banking/pix/scheduled/${id}`, {
        data: { reason },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-pix'] });
      queryClient.invalidateQueries({ queryKey: ['scheduled-pix-stats'] });
    },
  });
}

// Helper functions
export function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    SCHEDULED: 'Agendado',
    PROCESSING: 'Processando',
    COMPLETED: 'Concluído',
    FAILED: 'Falhou',
    CANCELLED: 'Cancelado',
  };
  return labels[status] || status;
}

export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    SCHEDULED: 'blue',
    PROCESSING: 'yellow',
    COMPLETED: 'green',
    FAILED: 'red',
    CANCELLED: 'gray',
  };
  return colors[status] || 'gray';
}

export function getKeyTypeLabel(keyType: string): string {
  const labels: Record<string, string> = {
    CPF: 'CPF',
    CNPJ: 'CNPJ',
    EMAIL: 'E-mail',
    PHONE: 'Telefone',
    EVP: 'Chave Aleatória',
  };
  return labels[keyType] || keyType;
}
